/**
 * Batch 2 PR #3 — org-level audit-log SIEM webhook contracts.
 *
 * Shared shapes for the per-org SIEM forwarding endpoints. Every
 * security buyer eventually asks the same question: "Can we ship
 * your audit log to our SIEM in near-real time?" These contracts
 * back the admin REST surface that lets them register one or
 * more HTTPS endpoints + the worker payload sent to each.
 *
 * See:
 *   - migration `149_audit_siem_endpoints.sql` (DB schema)
 *   - `packages/api/src/routes/audit-siem.ts` (REST surface)
 *   - `packages/api/src/workers/audit-siem-worker.ts` (delivery)
 *   - `packages/landing-page/src/features/dashboard/pages/OrgSecurityPage.tsx`
 *     (admin UI section)
 *   - `docs/handbook/runbooks/audit-siem-forwarding.md` (operator runbook)
 *
 * Hard rules:
 *   - `url` must be `https://` (the route refuses everything else and
 *     rejects RFC1918 / link-local / localhost on the way in).
 *   - `secret` is never returned by `GET`. Only its presence
 *     is exposed via `hasSecret`. Rotation = `PATCH { rotateSecret: true }`
 *     and the new value flows back exactly once on the PATCH response.
 *   - `actionFilter` is an allowlist of `audit_log.action` verbs. An
 *     empty array or omitted field = forward every action.
 */

import { z } from 'zod';

import { AiProvenanceSchema } from './audit-provenance';

const ACTION_FILTER_MAX = 200;
const URL_MAX = 2048;

export const AuditSiemEndpointSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  url: z.string().url().max(URL_MAX),
  enabled: z.boolean(),
  hasSecret: z.boolean(),
  actionFilter: z.array(z.string().min(1).max(200)).nullable(),
  lastForwardedAt: z.string().datetime().nullable(),
  lastSuccessAt: z.string().datetime().nullable(),
  lastFailureAt: z.string().datetime().nullable(),
  lastFailureReason: z.string().nullable(),
  consecutiveFailures: z.number().int().min(0),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  createdByUserId: z.string().uuid().nullable(),
});
export type AuditSiemEndpoint = z.infer<typeof AuditSiemEndpointSchema>;

/**
 * `POST /api/orgs/:orgId/audit-siem` body. Admin supplies the URL
 * and optional filter; the server generates the HMAC secret server
 * side (always, never accept a customer-supplied secret — clients
 * misuse weak entropy) and returns it in the response exactly once.
 */
export const CreateAuditSiemEndpointBodySchema = z.object({
  url: z.string().url().max(URL_MAX),
  enabled: z.boolean().optional(),
  actionFilter: z.array(z.string().min(1).max(200)).max(ACTION_FILTER_MAX).optional(),
});
export type CreateAuditSiemEndpointBody = z.infer<typeof CreateAuditSiemEndpointBodySchema>;

export const CreateAuditSiemEndpointResponseSchema = z.object({
  endpoint: AuditSiemEndpointSchema,
  /**
   * Returned exactly once on create + on secret rotation. Subsequent
   * GETs surface only `hasSecret: true`. Admin is responsible for
   * storing the value in their secret manager.
   */
  secret: z.string().min(32),
});
export type CreateAuditSiemEndpointResponse = z.infer<typeof CreateAuditSiemEndpointResponseSchema>;

export const PatchAuditSiemEndpointBodySchema = z
  .object({
    url: z.string().url().max(URL_MAX).optional(),
    enabled: z.boolean().optional(),
    actionFilter: z.array(z.string().min(1).max(200)).max(ACTION_FILTER_MAX).nullable().optional(),
    rotateSecret: z.boolean().optional(),
  })
  .refine(
    (b) => b.url !== undefined || b.enabled !== undefined || b.actionFilter !== undefined || b.rotateSecret === true,
    { message: 'At least one of url, enabled, actionFilter, or rotateSecret must be provided.' },
  );
export type PatchAuditSiemEndpointBody = z.infer<typeof PatchAuditSiemEndpointBodySchema>;

export const PatchAuditSiemEndpointResponseSchema = z.object({
  endpoint: AuditSiemEndpointSchema,
  /** Present only when `rotateSecret: true` was sent. */
  secret: z.string().min(32).optional(),
});
export type PatchAuditSiemEndpointResponse = z.infer<typeof PatchAuditSiemEndpointResponseSchema>;

export const ListAuditSiemEndpointsResponseSchema = z.object({
  items: z.array(AuditSiemEndpointSchema),
});
export type ListAuditSiemEndpointsResponse = z.infer<typeof ListAuditSiemEndpointsResponseSchema>;

/**
 * Worker delivery payload. The audit-siem worker batches up to
 * `AUDIT_SIEM_BATCH_SIZE` rows per tick per endpoint and POSTs a
 * single body of this shape. Customer SIEM ingesters can rely on
 * the shape being stable; future versions add new fields, never
 * rename existing ones.
 */
export const AuditSiemDeliveryEventSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  actorUserId: z.string().uuid().nullable(),
  actorRole: z.string().nullable(),
  action: z.string(),
  entityType: z.string().nullable(),
  entityId: z.string().nullable(),
  entityName: z.string().nullable(),
  targetOrgId: z.string().uuid().nullable(),
  bypassReasons: z.array(z.string()).nullable(),
  meta: z.unknown().nullable(),
  count: z.number().int().min(1),
  occurredAt: z.string().datetime(),
  /**
   * Present when the row was produced by an AI call. Lets a
   * customer SIEM build "all AI-generated artifacts touching org X
   * last quarter" reports without joining back to Rynora. See
   * `audit-provenance.ts` for the shape.
   */
  aiProvenance: AiProvenanceSchema.nullable().optional(),
});
export type AuditSiemDeliveryEvent = z.infer<typeof AuditSiemDeliveryEventSchema>;

export const AuditSiemDeliveryBodySchema = z.object({
  /** Stable schema version; bumped on incompatible changes only. */
  version: z.literal(1),
  endpointId: z.string().uuid(),
  orgId: z.string().uuid(),
  events: z.array(AuditSiemDeliveryEventSchema),
  /** UTC timestamp at which the worker assembled the batch. */
  emittedAt: z.string().datetime(),
});
export type AuditSiemDeliveryBody = z.infer<typeof AuditSiemDeliveryBodySchema>;
